import { identity } from 'ramda';
import type { ClientError } from '@core/api/ClientError';
import { isHttpError, throwError, isNetworkError } from '@core/api/ClientError';
import type { Client, ClientRequestConfig } from '@core/api/utils';
import { pipeDataTwice, pipeError } from '@core/api/utils';

export const toSetNewPasswordURL = (userId: string) =>
  `/api/user/${userId}/set_password_for_new_user`;

export enum ErrorCode {
  PasswordAlreadyExists = 'passwordAlreadyExists',
  PasswordDoesntValidate = 'passwordDoesntValidate',
  PasswordNotSent = 'passwordNotSent',
  PasswordTooShort = 'passwordTooShort',
  PasswordBlacklisted = 'passwordBlacklisted',
  NetworkError = 'networkError',
  Default = 'default',
}

const isSetNewPasswordErrorCode = (code: string): code is ErrorCode =>
  (Object.values(ErrorCode) as string[]).includes(code);

export const toSetNewUserPasswordErrorCodeOrDefault = (code?: string): ErrorCode => {
  if (!code || !isSetNewPasswordErrorCode(code)) {
    return ErrorCode.Default;
  } else {
    return code;
  }
};

const apiErrorMap = {
  3070: ErrorCode.PasswordAlreadyExists,
  3080: ErrorCode.PasswordDoesntValidate,
  3090: ErrorCode.PasswordNotSent,
  3100: ErrorCode.PasswordTooShort,
  3110: ErrorCode.PasswordBlacklisted,
};

const toErrorCode = (error: ClientError): ErrorCode => {
  const apiErrorCode = isHttpError(error) ? error.responseBody.errorCode : undefined;

  return isNetworkError(error)
    ? ErrorCode.NetworkError
    : // @ts-expect-error (TS7053): Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ 3070: ErrorCode; 3080: ErrorCode; 3090: ErrorCode; 3100: ErrorCode; 3110: ErrorCode; }'.
      apiErrorMap[apiErrorCode] || ErrorCode.Default;
};

export const setPasswordForNewUser = (
  api: Client,
  userId: string,
  password: string,
  config?: ClientRequestConfig,
) =>
  api
    .post(toSetNewPasswordURL(userId), { password }, config)
    .then(pipeDataTwice(identity))
    .catch(pipeError(throwError<ErrorCode>(toErrorCode)));
